import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CountryReportResponseV4 } from '../../../../services/cm-group-report/response/CountryReportResponseV4';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

interface CountryReportTableV4Props {
    data: CountryReportResponseV4;
    orderBy: string;
    sort: 'asc' | 'desc';
    setOrderBy: (field: string) => void;
    setSort: (direction: 'asc' | 'desc') => void;
}

const CountryReportTableV4: React.FC<CountryReportTableV4Props> = ({ data, orderBy, sort, setOrderBy, setSort }) => {
    const columns = [
        { label: "Country", key: "country" },
        { label: "Number of Reservations", key: "nb_reservations" },
        { label: "Amount (IDR)", key: "amount" },
    ];

    const handleSort = (key: string) => {
        if (orderBy === key) {
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(key);
            setSort('desc'); // Lalai kepada 'desc' untuk lajur baharu
        }
    };

    const renderSortIcon = (key: string) => {
        if (orderBy !== key) return null;
        return sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const formatRowValue = (row: any, colKey: string) => {
        const value = row[colKey as keyof typeof row];
        if (colKey === 'amount') {
            return formatPrice(Number(value) || 0);
        }
        return value;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Country Report", 14, 10);
        const head = [columns.map(col => col.label)];
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const foot = [[
            "Total",
            data.total.nb_reservations,
            formatPrice(Number(data.total.amount) || 0)
        ]];

        autoTable(doc, { head, body, foot });
        doc.save("CountryReportV4.pdf");
    };

    const exportToExcel = () => {
        const header = columns.map(col => col.label);
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const footer = [
            "Total",
            data.total.nb_reservations,
            formatPrice(Number(data.total.amount) || 0)
        ];
        const wsData = [header, ...body, footer];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Country Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "CountryReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                                    {col.label} {renderSortIcon(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col) => (
                                        <td key={col.key}>{formatRowValue(row, col.key)}</td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={columns.length} className="text-center">No data available</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><strong>{data.total.nb_reservations}</strong></td>
                            <td><strong>{formatPrice(Number(data.total.amount) || 0)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default CountryReportTableV4;